import { Logo, LogoIcon } from './Logo';
import { AnimatedLogo, AnimatedLogoIcon, LoadingLogo } from './AnimatedLogo';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { FestiveDecoration, FestiveDivider, FestiveCorner } from './FestiveDecoration';
import { ArrowLeft, Copy, Check } from 'lucide-react';
import { useState } from 'react';

interface DesignShowcaseProps {
  onNavigate: (screen: string) => void;
  showBackButton?: boolean;
}

export function DesignShowcase({ onNavigate, showBackButton = false }: DesignShowcaseProps) {
  const [copiedColor, setCopiedColor] = useState<string | null>(null);

  const copyToClipboard = async (hex: string) => {
    try {
      await navigator.clipboard.writeText(hex);
      setCopiedColor(hex);
      setTimeout(() => setCopiedColor(null), 2000);
    } catch {
      // Fallback for browsers without clipboard API support
      console.log('Clipboard copy failed, hex code:', hex);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-secondary/30 py-12 px-4 print:bg-white print:py-4">
      <div className="max-w-7xl mx-auto">
        {/* Back Button - only shown when navigating from admin */}
        {showBackButton && (
          <Button
            variant="ghost"
            onClick={() => onNavigate('admin')}
            className="mb-4 print:hidden"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </Button>
        )}

        <Card className="p-8 mb-8">
          <h1 className="text-4xl mb-4">Better to Give than Receive Design System</h1>
          <p className="text-muted-foreground">
            Complete design system documentation with colors, typography, components, and patterns for the Better to Give than Receive platform.
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Last Updated: November 2025 • Version 1.0
          </p>
        </Card>

        {/* Color Palette with Hex Codes */}
        <Card className="p-8 mb-8">
          <h2 className="text-2xl mb-2">Color Palette</h2>
          <p className="text-muted-foreground mb-6">Click any color to copy the hex code to clipboard.</p>
          
          <div className="space-y-8">
            {/* Primary Colors */}
            <div>
              <h3 className="text-lg mb-4 font-semibold">Primary Colors</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <button
                  onClick={() => copyToClipboard('#2d5016')}
                  className="space-y-2 text-left hover:scale-105 transition-transform"
                >
                  <div className="bg-primary h-24 rounded-lg border-2 flex items-center justify-center">
                    {copiedColor === '#2d5016' ? (
                      <Check className="w-6 h-6 text-white" />
                    ) : (
                      <Copy className="w-6 h-6 text-white opacity-0 hover:opacity-100 transition-opacity" />
                    )}
                  </div>
                  <p className="font-medium">Forest Green</p>
                  <p className="text-xs text-muted-foreground font-mono">#2d5016</p>
                  <p className="text-xs text-muted-foreground">Primary</p>
                </button>
                
                <button
                  onClick={() => copyToClipboard('#c41e3a')}
                  className="space-y-2 text-left hover:scale-105 transition-transform"
                >
                  <div className="bg-destructive h-24 rounded-lg border-2 flex items-center justify-center">
                    {copiedColor === '#c41e3a' ? (
                      <Check className="w-6 h-6 text-white" />
                    ) : (
                      <Copy className="w-6 h-6 text-white opacity-0 hover:opacity-100 transition-opacity" />
                    )}
                  </div>
                  <p className="font-medium">Holiday Red</p>
                  <p className="text-xs text-muted-foreground font-mono">#c41e3a</p>
                  <p className="text-xs text-muted-foreground">Destructive</p>
                </button>

                <button
                  onClick={() => copyToClipboard('#f4a460')}
                  className="space-y-2 text-left hover:scale-105 transition-transform"
                >
                  <div style={{ backgroundColor: '#f4a460' }} className="h-24 rounded-lg border-2 flex items-center justify-center">
                    {copiedColor === '#f4a460' ? (
                      <Check className="w-6 h-6 text-white" />
                    ) : (
                      <Copy className="w-6 h-6 text-white opacity-0 hover:opacity-100 transition-opacity" />
                    )}
                  </div>
                  <p className="font-medium">Sandy Brown</p>
                  <p className="text-xs text-muted-foreground font-mono">#f4a460</p>
                  <p className="text-xs text-muted-foreground">Ring/Focus</p>
                </button>

                <button
                  onClick={() => copyToClipboard('#f5e6d3')}
                  className="space-y-2 text-left hover:scale-105 transition-transform"
                >
                  <div className="bg-secondary h-24 rounded-lg border-2 flex items-center justify-center">
                    {copiedColor === '#f5e6d3' ? (
                      <Check className="w-6 h-6 text-foreground" />
                    ) : (
                      <Copy className="w-6 h-6 text-foreground opacity-0 hover:opacity-100 transition-opacity" />
                    )}
                  </div>
                  <p className="font-medium">Warm Beige</p>
                  <p className="text-xs text-muted-foreground font-mono">#f5e6d3</p>
                  <p className="text-xs text-muted-foreground">Secondary</p>
                </button>
              </div>
            </div>

            {/* Background & Surface Colors */}
            <div>
              <h3 className="text-lg mb-4 font-semibold">Background & Surface Colors</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <button
                  onClick={() => copyToClipboard('#fefdfb')}
                  className="space-y-2 text-left hover:scale-105 transition-transform"
                >
                  <div style={{ backgroundColor: '#fefdfb' }} className="h-24 rounded-lg border-2 flex items-center justify-center">
                    {copiedColor === '#fefdfb' ? (
                      <Check className="w-6 h-6 text-foreground" />
                    ) : (
                      <Copy className="w-6 h-6 text-foreground opacity-0 hover:opacity-100 transition-opacity" />
                    )}
                  </div>
                  <p className="font-medium">Off White</p>
                  <p className="text-xs text-muted-foreground font-mono">#fefdfb</p>
                  <p className="text-xs text-muted-foreground">Background</p>
                </button>

                <button
                  onClick={() => copyToClipboard('#ffffff')}
                  className="space-y-2 text-left hover:scale-105 transition-transform"
                >
                  <div style={{ backgroundColor: '#ffffff' }} className="h-24 rounded-lg border-2 flex items-center justify-center">
                    {copiedColor === '#ffffff' ? (
                      <Check className="w-6 h-6 text-foreground" />
                    ) : (
                      <Copy className="w-6 h-6 text-foreground opacity-0 hover:opacity-100 transition-opacity" />
                    )}
                  </div>
                  <p className="font-medium">White</p>
                  <p className="text-xs text-muted-foreground font-mono">#ffffff</p>
                  <p className="text-xs text-muted-foreground">Card</p>
                </button>

                <button
                  onClick={() => copyToClipboard('#fef3e9')}
                  className="space-y-2 text-left hover:scale-105 transition-transform"
                >
                  <div className="bg-accent h-24 rounded-lg border-2 flex items-center justify-center">
                    {copiedColor === '#fef3e9' ? (
                      <Check className="w-6 h-6 text-foreground" />
                    ) : (
                      <Copy className="w-6 h-6 text-foreground opacity-0 hover:opacity-100 transition-opacity" />
                    )}
                  </div>
                  <p className="font-medium">Warm Cream</p>
                  <p className="text-xs text-muted-foreground font-mono">#fef3e9</p>
                  <p className="text-xs text-muted-foreground">Accent</p>
                </button>

                <button
                  onClick={() => copyToClipboard('#f5ebe0')}
                  className="space-y-2 text-left hover:scale-105 transition-transform"
                >
                  <div className="bg-muted h-24 rounded-lg border-2 flex items-center justify-center">
                    {copiedColor === '#f5ebe0' ? (
                      <Check className="w-6 h-6 text-foreground" />
                    ) : (
                      <Copy className="w-6 h-6 text-foreground opacity-0 hover:opacity-100 transition-opacity" />
                    )}
                  </div>
                  <p className="font-medium">Soft Tan</p>
                  <p className="text-xs text-muted-foreground font-mono">#f5ebe0</p>
                  <p className="text-xs text-muted-foreground">Muted</p>
                </button>
              </div>
            </div>

            {/* Text Colors */}
            <div>
              <h3 className="text-lg mb-4 font-semibold">Text Colors</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <button
                  onClick={() => copyToClipboard('#2d3319')}
                  className="space-y-2 text-left hover:scale-105 transition-transform"
                >
                  <div style={{ backgroundColor: '#2d3319' }} className="h-24 rounded-lg border-2 flex items-center justify-center">
                    {copiedColor === '#2d3319' ? (
                      <Check className="w-6 h-6 text-white" />
                    ) : (
                      <Copy className="w-6 h-6 text-white opacity-0 hover:opacity-100 transition-opacity" />
                    )}
                  </div>
                  <p className="font-medium">Dark Olive</p>
                  <p className="text-xs text-muted-foreground font-mono">#2d3319</p>
                  <p className="text-xs text-muted-foreground">Foreground</p>
                </button>

                <button
                  onClick={() => copyToClipboard('#6b7048')}
                  className="space-y-2 text-left hover:scale-105 transition-transform"
                >
                  <div style={{ backgroundColor: '#6b7048' }} className="h-24 rounded-lg border-2 flex items-center justify-center">
                    {copiedColor === '#6b7048' ? (
                      <Check className="w-6 h-6 text-white" />
                    ) : (
                      <Copy className="w-6 h-6 text-white opacity-0 hover:opacity-100 transition-opacity" />
                    )}
                  </div>
                  <p className="font-medium">Olive Gray</p>
                  <p className="text-xs text-muted-foreground font-mono">#6b7048</p>
                  <p className="text-xs text-muted-foreground">Muted Foreground</p>
                </button>

                <button
                  onClick={() => copyToClipboard('#ffffff')}
                  className="space-y-2 text-left hover:scale-105 transition-transform"
                >
                  <div style={{ backgroundColor: '#2d5016' }} className="h-24 rounded-lg border-2 flex items-center justify-center">
                    <span className="text-white font-medium">Aa</span>
                  </div>
                  <p className="font-medium">White on Primary</p>
                  <p className="text-xs text-muted-foreground font-mono">#ffffff</p>
                  <p className="text-xs text-muted-foreground">Primary Foreground</p>
                </button>
              </div>
            </div>
          </div>
        </Card>

        {/* Typography */}
        <Card className="p-8 mb-8">
          <h2 className="text-2xl mb-2">Typography</h2>
          <p className="text-muted-foreground mb-6">Font hierarchy and text styles used throughout the platform.</p>
          
          <div className="space-y-6">
            <div className="p-4 border rounded-lg">
              <h1 className="text-4xl mb-2">Heading 1 (text-4xl)</h1>
              <p className="text-xs text-muted-foreground font-mono">Font Size: 2.25rem (36px) • Font Weight: 500</p>
            </div>
            <div className="p-4 border rounded-lg">
              <h2 className="text-2xl mb-2">Heading 2 (text-2xl)</h2>
              <p className="text-xs text-muted-foreground font-mono">Font Size: 1.5rem (24px) • Font Weight: 500</p>
            </div>
            <div className="p-4 border rounded-lg">
              <h3 className="text-xl mb-2">Heading 3 (text-xl)</h3>
              <p className="text-xs text-muted-foreground font-mono">Font Size: 1.25rem (20px) • Font Weight: 500</p>
            </div>
            <div className="p-4 border rounded-lg">
              <h4 className="text-lg mb-2">Heading 4 (text-lg)</h4>
              <p className="text-xs text-muted-foreground font-mono">Font Size: 1.125rem (18px) • Font Weight: 500</p>
            </div>
            <div className="p-4 border rounded-lg">
              <p className="text-base mb-2">Body Text (text-base)</p>
              <p className="text-xs text-muted-foreground font-mono">Font Size: 1rem (16px) • Font Weight: 400</p>
            </div>
            <div className="p-4 border rounded-lg">
              <p className="text-sm mb-2">Small Text (text-sm)</p>
              <p className="text-xs text-muted-foreground font-mono">Font Size: 0.875rem (14px) • Font Weight: 400</p>
            </div>
            <div className="p-4 border rounded-lg">
              <p className="text-xs mb-2">Extra Small Text (text-xs)</p>
              <p className="text-xs text-muted-foreground font-mono">Font Size: 0.75rem (12px) • Font Weight: 400</p>
            </div>
            <div className="p-4 border rounded-lg">
              <p className="text-muted-foreground mb-2">Muted Text (text-muted-foreground)</p>
              <p className="text-xs text-muted-foreground font-mono">Color: #6b7048 • Used for secondary content</p>
            </div>
          </div>
        </Card>

        {/* Logo Showcase */}
        <Card className="p-8 mb-8">
          <h2 className="text-2xl mb-2">Logo Variations</h2>
          <p className="text-muted-foreground mb-6">Logo components available in multiple sizes and variants.</p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-lg mb-4 font-semibold">Full Logo</h3>
              <div className="bg-white p-8 rounded-lg border-2">
                <Logo size="lg" />
              </div>
            </div>

            <div>
              <h3 className="text-lg mb-4 font-semibold">Logo Icon Only (sm, md, lg)</h3>
              <div className="bg-white p-8 rounded-lg border-2 flex gap-8 items-center">
                <div className="text-center">
                  <LogoIcon size="sm" />
                  <p className="text-xs text-muted-foreground mt-2">Small</p>
                </div>
                <div className="text-center">
                  <LogoIcon size="md" />
                  <p className="text-xs text-muted-foreground mt-2">Medium</p>
                </div>
                <div className="text-center">
                  <LogoIcon size="lg" />
                  <p className="text-xs text-muted-foreground mt-2">Large</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg mb-4 font-semibold">Animated Variants</h3>
              <div className="bg-white p-8 rounded-lg border-2 flex gap-8 items-center flex-wrap">
                <div className="text-center">
                  <AnimatedLogo size="lg" />
                  <p className="text-xs text-muted-foreground mt-2">Animated Logo</p>
                </div>
                <div className="text-center">
                  <AnimatedLogoIcon size="md" />
                  <p className="text-xs text-muted-foreground mt-2">Animated Icon</p>
                </div>
                <div className="text-center">
                  <LoadingLogo />
                  <p className="text-xs text-muted-foreground mt-2">Loading Logo</p>
                </div>
              </div>
            </div>

          </div>
        </Card>

        {/* Button Components */}
        <Card className="p-8 mb-8">
          <h2 className="text-2xl mb-2">Button Components</h2>
          <p className="text-muted-foreground mb-6">All button variants and sizes available in the design system.</p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-lg mb-4 font-semibold">Button Variants</h3>
              <div className="flex flex-wrap gap-4 items-center">
                <div className="text-center">
                  <Button variant="default">Default</Button>
                  <p className="text-xs text-muted-foreground mt-2">default</p>
                </div>
                <div className="text-center">
                  <Button variant="secondary">Secondary</Button>
                  <p className="text-xs text-muted-foreground mt-2">secondary</p>
                </div>
                <div className="text-center">
                  <Button variant="outline">Outline</Button>
                  <p className="text-xs text-muted-foreground mt-2">outline</p>
                </div>
                <div className="text-center">
                  <Button variant="ghost">Ghost</Button>
                  <p className="text-xs text-muted-foreground mt-2">ghost</p>
                </div>
                <div className="text-center">
                  <Button variant="link">Link</Button>
                  <p className="text-xs text-muted-foreground mt-2">link</p>
                </div>
                <div className="text-center">
                  <Button variant="destructive">Destructive</Button>
                  <p className="text-xs text-muted-foreground mt-2">destructive</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg mb-4 font-semibold">Button Sizes</h3>
              <div className="flex flex-wrap gap-4 items-center">
                <div className="text-center">
                  <Button size="sm">Small</Button>
                  <p className="text-xs text-muted-foreground mt-2">sm (h-8)</p>
                </div>
                <div className="text-center">
                  <Button size="default">Default</Button>
                  <p className="text-xs text-muted-foreground mt-2">default (h-9)</p>
                </div>
                <div className="text-center">
                  <Button size="lg">Large</Button>
                  <p className="text-xs text-muted-foreground mt-2">lg (h-10)</p>
                </div>
                <div className="text-center">
                  <Button size="icon"><Copy className="h-4 w-4" /></Button>
                  <p className="text-xs text-muted-foreground mt-2">icon</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg mb-4 font-semibold">Button States</h3>
              <div className="flex flex-wrap gap-4 items-center">
                <div className="text-center">
                  <Button>Enabled</Button>
                  <p className="text-xs text-muted-foreground mt-2">Normal</p>
                </div>
                <div className="text-center">
                  <Button disabled>Disabled</Button>
                  <p className="text-xs text-muted-foreground mt-2">disabled</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Form Components */}
        <Card className="p-8 mb-8">
          <h2 className="text-2xl mb-2">Form Components</h2>
          <p className="text-muted-foreground mb-6">Input fields and form elements used throughout the platform.</p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-lg mb-4 font-semibold">Text Inputs</h3>
              <div className="grid gap-4 max-w-md">
                <div>
                  <label className="text-sm font-medium mb-2 block">Default Input</label>
                  <Input placeholder="Enter text..." />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Disabled Input</label>
                  <Input placeholder="Disabled input" disabled />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Email Input</label>
                  <Input type="email" placeholder="email@example.com" />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg mb-4 font-semibold">Checkboxes</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Checkbox id="check1" />
                  <label htmlFor="check1" className="text-sm">Unchecked checkbox</label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="check2" defaultChecked />
                  <label htmlFor="check2" className="text-sm">Checked checkbox</label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="check3" disabled />
                  <label htmlFor="check3" className="text-sm text-muted-foreground">Disabled checkbox</label>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Badge Components */}
        <Card className="p-8 mb-8">
          <h2 className="text-2xl mb-2">Badge Components</h2>
          <p className="text-muted-foreground mb-6">Status indicators and labels.</p>
          
          <div className="flex flex-wrap gap-4">
            <div className="text-center">
              <Badge>Default</Badge>
              <p className="text-xs text-muted-foreground mt-2">default</p>
            </div>
            <div className="text-center">
              <Badge variant="secondary">Secondary</Badge>
              <p className="text-xs text-muted-foreground mt-2">secondary</p>
            </div>
            <div className="text-center">
              <Badge variant="outline">Outline</Badge>
              <p className="text-xs text-muted-foreground mt-2">outline</p>
            </div>
            <div className="text-center">
              <Badge variant="destructive">Destructive</Badge>
              <p className="text-xs text-muted-foreground mt-2">destructive</p>
            </div>
            <div className="text-center">
              <Badge className="bg-primary/80 text-white">Success</Badge>
              <p className="text-xs text-muted-foreground mt-2">custom (primary/80)</p>
            </div>
            <div className="text-center">
              <Badge style={{ backgroundColor: '#f4a460' }} className="text-white">Warning</Badge>
              <p className="text-xs text-muted-foreground mt-2">custom (ring)</p>
            </div>
          </div>
        </Card>

        {/* Card Components */}
        <Card className="p-8 mb-8">
          <h2 className="text-2xl mb-2">Card Components</h2>
          <p className="text-muted-foreground mb-6">Container components for grouping content.</p>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="text-lg mb-2">Basic Card</h3>
              <p className="text-sm text-muted-foreground">
                A simple card with padding. Cards have a white background, subtle border, and rounded corners.
              </p>
            </Card>
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-lg mb-2">Interactive Card</h3>
              <p className="text-sm text-muted-foreground">
                Cards can have hover effects for interactive elements.
              </p>
            </Card>
          </div>
        </Card>

        {/* Festive Decorations */}
        <Card className="p-8 mb-8">
          <h2 className="text-2xl mb-2">Festive Decorations</h2>
          <p className="text-muted-foreground mb-6">Seasonal decorative elements that add charm to the platform.</p>
          
          <div className="relative bg-white p-12 rounded-lg border-2 min-h-[300px]">
            <FestiveDecoration variant="snowflakes" position="top-left" />
            <FestiveDecoration variant="gifts" position="bottom-right" />
            <FestiveCorner corner="top-left" />
            <FestiveCorner corner="bottom-right" />
            <div className="text-center">
              <p className="text-muted-foreground">Decorative elements add festive charm to any component</p>
            </div>
            <FestiveDivider className="my-8" />
            <p className="text-sm text-center text-muted-foreground">Festive divider component above</p>
          </div>
        </Card>

        {/* Spacing & Layout */}
        <Card className="p-8 mb-8">
          <h2 className="text-2xl mb-2">Spacing & Layout</h2>
          <p className="text-muted-foreground mb-6">Standard spacing values and layout patterns.</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg mb-4 font-semibold">Border Radius</h3>
              <p className="text-sm text-muted-foreground mb-4">Base radius: 0.75rem (12px)</p>
              <div className="flex flex-wrap gap-4">
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary rounded-sm"></div>
                  <p className="text-xs text-muted-foreground mt-2">rounded-sm</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary rounded-md"></div>
                  <p className="text-xs text-muted-foreground mt-2">rounded-md</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary rounded-lg"></div>
                  <p className="text-xs text-muted-foreground mt-2">rounded-lg</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary rounded-xl"></div>
                  <p className="text-xs text-muted-foreground mt-2">rounded-xl</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary rounded-full"></div>
                  <p className="text-xs text-muted-foreground mt-2">rounded-full</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg mb-4 font-semibold">Spacing Scale</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-4">
                  <div className="w-2 h-4 bg-primary"></div>
                  <span className="text-sm">2 (0.5rem / 8px)</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-4 h-4 bg-primary"></div>
                  <span className="text-sm">4 (1rem / 16px)</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-6 h-4 bg-primary"></div>
                  <span className="text-sm">6 (1.5rem / 24px)</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-8 h-4 bg-primary"></div>
                  <span className="text-sm">8 (2rem / 32px)</span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Design Guidelines */}
        <Card className="p-8">
          <h2 className="text-2xl mb-2">Design Guidelines</h2>
          <p className="text-muted-foreground mb-6">Key principles for maintaining consistency.</p>
          
          <div className="space-y-4">
            <div className="p-4 bg-secondary/50 rounded-lg">
              <h3 className="font-semibold mb-2">🎨 Mobile-First Responsive Design</h3>
              <p className="text-sm text-muted-foreground">
                All components are built mobile-first and scale up for larger screens using Tailwind breakpoints (sm, md, lg, xl).
              </p>
            </div>
            <div className="p-4 bg-secondary/50 rounded-lg">
              <h3 className="font-semibold mb-2">🌲 Festive Color Theme</h3>
              <p className="text-sm text-muted-foreground">
                The color palette draws inspiration from holiday themes: forest greens, holiday reds, warm golds, and cozy beige tones.
              </p>
            </div>
            <div className="p-4 bg-secondary/50 rounded-lg">
              <h3 className="font-semibold mb-2">✨ Consistent Spacing</h3>
              <p className="text-sm text-muted-foreground">
                Use the 4px base unit system. Prefer multiples of 4 (4, 8, 12, 16, 24, 32) for consistent spacing.
              </p>
            </div>
            <div className="p-4 bg-secondary/50 rounded-lg">
              <h3 className="font-semibold mb-2">📝 Brand Name Usage</h3>
              <p className="text-sm text-muted-foreground">
                Always use the full name "Better to Give than Receive" in user-facing text. Never abbreviate to "BtGtR" in emails or UI.
              </p>
            </div>
            <div className="p-4 bg-secondary/50 rounded-lg">
              <h3 className="font-semibold mb-2">♿ Accessibility</h3>
              <p className="text-sm text-muted-foreground">
                Maintain sufficient color contrast. Use semantic HTML elements. All interactive elements have visible focus states.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
